/**
 * La línea de la conexión con Snov, arriba de cada pantalla de Emails: de cuándo son los datos, si se está
 * actualizando (con el avance) y, para el equipo, «Actualizar ahora», la última falla y las campañas sin comercio.
 * Sin las claves en el servidor, un aviso en lugar de la línea.
 *
 * El «hace X» se recalcula cada 30 s (si no, quedaba congelado con la página abierta). El avance no es una región viva:
 * el lector de pantalla anuncia sólo cuando empieza y cuando termina, no cada 3 s.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, PlugZap, RefreshCw } from "lucide-react";
import { useEstadoEmails } from "../contexto";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { haceTexto } from "../../wa-oficial/logic/estadisticas";
import { R_EMAILS } from "../logic/emails";
import { num, plural } from "../../campana-nueva/utils/format";

export const CADA_MS_RELOJ = 30_000;

export function EstadoSnov() {
  const { estado, error, actualizando, detenido, actualizar, recargar } = useEstadoEmails();
  const equipo = useEsEquipoDeenex();
  const [aviso, setAviso] = useState("");
  const [pidiendo, setPidiendo] = useState(false);
  const [ahora, setAhora] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), CADA_MS_RELOJ);
    return () => clearInterval(t);
  }, []);

  if (!estado) {
    return error ? (
      <p className="text-xs text-amber-700" role="alert">
        No pudimos leer la conexión con Snov: {error}{" "}
        <button type="button" onClick={recargar} className="font-semibold underline">Reintentar</button>
      </p>
    ) : null;
  }

  if (!estado.configurado) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
        <PlugZap size={18} className="mt-0.5 flex-shrink-0 text-amber-600" />
        <div>
          <p className="font-semibold">Emails todavía no está conectado a Snov.</p>
          <p className="mt-0.5 text-amber-800">
            {equipo
              ? "Faltan las claves de la API de Snov en el servidor. El detalle está en Emails → Conexión; con las claves cargadas, se trae todo solo."
              : "Cuando el equipo de Deenex lo conecte, acá vas a ver cómo les fue a tus campañas de mail."}
          </p>
        </div>
      </div>
    );
  }

  const apretar = async () => {
    if (pidiendo || actualizando) return;
    setPidiendo(true);
    setAviso(await actualizar());
    setPidiendo(false);
  };

  const hace = haceTexto(estado.actualizadoEn, ahora);
  const e = estado.equipo;
  const avance = actualizando && e?.total ? ` (${num(e.hechas)} de ${num(e.total)}${e.paso ? `: ${e.paso}` : ""})` : "";

  return (
    <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-xs text-slate-500">
      <span className="sr-only" role="status">{actualizando ? "Actualizando desde Snov" : hace ? "Datos de Snov al día" : ""}</span>
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${actualizando ? "bg-violet-500 animate-pulse" : hace ? "bg-emerald-500" : "bg-slate-300"}`} />
        {actualizando ? `Actualizando desde Snov…${avance}` : hace ? `Datos de Snov, actualizados ${hace}` : "Todavía no se trajo nada de Snov"}
      </span>
      <span className="text-slate-400">Se actualiza sola cada {num(estado.cadaMin)} min. Snov publica lo nuevo con hasta 20 min de atraso.</span>
      {equipo && (
        <button type="button" onClick={() => void apretar()} disabled={pidiendo || actualizando}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {pidiendo || actualizando ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Actualizar ahora
        </button>
      )}
      {error && (
        <span className="inline-flex items-center gap-1 text-amber-700" role="alert">
          <AlertTriangle size={12} />
          {detenido ? `Dejamos de mirar el estado de Snov: ${error}` : `No pudimos leer el estado de Snov: ${error}`}
          {detenido && <button type="button" onClick={recargar} className="font-semibold underline">Reintentar</button>}
        </span>
      )}
      {equipo && e?.ok === false && e.error && (
        <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={12} /> La última actualización falló: {e.error}</span>
      )}
      {equipo && !!e?.sinAsignar && (
        <Link to={`${R_EMAILS}/conexion`} className="font-semibold text-violet-700 hover:underline">
          {num(e.sinAsignar)} {plural(e.sinAsignar, "campaña sin comercio", "campañas sin comercio")}
        </Link>
      )}
      {aviso && <span role="alert" className="text-amber-700">{aviso}</span>}
    </div>
  );
}

export default EstadoSnov;
